import { DocumentUploadDropzone } from "@/components/shared/dropzones/document-upload-dropzone";
import { InvoiceOverviewTable } from "@/features/invoice-overview/invoiceOverviewTable";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoicesProvider } from "@/providers/invoices";
import { useTenant } from "@/providers/session";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { useMemo } from "react";

const storageKey = "invoices";

export const Route = createFileRoute(
  "/_authenticated/$company/buchhaltung/rechnungen",
)({
  component: InvoiceOverviewPage,
  staticData: {
    breadcrumb: ["all_invoices"],
  },
});

function InvoiceOverviewPage() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const { isSuperAdmin } = usePermissions();

  const { page, setPage, limit, setLimit } = useTablePagination();
  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    filters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
  });

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    page,
    global: globalSearch,
    query: serverSideFilters,
    sort,
    order,
    limit,
  });

  const columnVisibility = useMemo(
    () => ({
      type: true,
      invoiceNo: true,
      invoiceDate: true,
      startDate: true,
      expenseAccount: true,
      datevBu: true,
      tags: true,
      createdAt: true,
      status: true,
      reviewType: false,
      ocrReviewed: false,
      isBooking: false,
      isDuplicate: false,
    }),
    [],
  );

  return (
    <div className="flex-1 px-4 pb-4 h-full">
      <DocumentUploadDropzone tenantId={tenantId}>
        <div className="flex flex-col h-full gap-4">
          <UsageHelperBanner label={t("todos.all_invoices_explainer")} />
          <div className="flex-1 min-h-0">
            <InvoicesProvider invoices={invoices} loading={loading}>
              <InvoiceOverviewTable
                tableName="quickreview"
                showConfidences
                columnVisibility={columnVisibility}
                filtersConfig={{
                  showTypeFilter: true,
                  showOcrReviewedFilter: isSuperAdmin,
                  showUploadButton: false,
                  autoFocus: true,
                }}
                totalItems={pagination.totalItems}
                currentPage={page}
                totalPages={pagination.totalPages}
                pageSize={limit}
                onPageChange={setPage}
                onPageSizeChange={setLimit}
                filters={filters}
                setFilters={setFilters}
                globalSearch={globalSearch}
                setGlobalSearch={setGlobalSearch}
                sorting={sorting}
                onSortingChange={onSortingChange}
                error={error}
                initialServerSideFilters={initialServerSideFilters}
                globalServerSideSearch={globalSearch}
                serverSideFilters={serverSideFilters}
                resetFilters={resetFilters}
                emptyStateComponent={<NoRecordsEmpty state="no-records" />}
              />
            </InvoicesProvider>
          </div>
        </div>
      </DocumentUploadDropzone>
    </div>
  );
}
